import { useCallback, useEffect, useRef, useState } from "react";
import { Button, ButtonGroup, Spinner } from "react-bootstrap";
import styled from "styled-components";

import { CommonMomentSummary } from "../types";
import { TooltipProvider } from "@bagel-web/components";

const MomentNavContainer = styled.span`
  display: flex;
  gap: 20px;
`;

const EARLIEST_TOOLTIP = "Earliest";
const LATEST_TOOLTIP = "Latest";

const MomentNavigator = ({
  startOfStream,
  endOfStream,
  moments,
  refetch,
  isRefetching,
  reverse,
}: {
  startOfStream: HTMLElement | null;
  endOfStream: HTMLElement | null;
  moments: CommonMomentSummary[];
  refetch: () => void;
  isRefetching: boolean;
  reverse: boolean;
}) => {
  const ignoreScrollEventsTimer = useRef<null | number>(null);
  const batchScrollEventsTimer = useRef<null | number>(null);

  const [currentMomentIndex, setCurrentMomentIndex] = useState(0);

  const hasMoments = moments.length > 0;

  const pauseScrollHandling = () => {
    if (ignoreScrollEventsTimer.current) {
      clearTimeout(ignoreScrollEventsTimer.current);
    }
    ignoreScrollEventsTimer.current = window.setTimeout(() => {
      ignoreScrollEventsTimer.current = null;
    }, 1000);
  };

  const scrollToTop = () => {
    pauseScrollHandling();
    startOfStream?.scrollIntoView({ behavior: "smooth" });
    setCurrentMomentIndex(0);
  };

  const scrollToBottom = () => {
    pauseScrollHandling();
    endOfStream?.scrollIntoView({ behavior: "smooth" });
    setCurrentMomentIndex(moments.length - 1);
  };

  const scrollToMoment = (momentId: number | undefined) => {
    pauseScrollHandling();
    window.document
      .getElementById(`moment-${momentId}`)
      ?.scrollIntoView({ behavior: "smooth" });
  };

  const handleScroll = useCallback(() => {
    if (ignoreScrollEventsTimer.current !== null) {
      return;
    }

    if (batchScrollEventsTimer.current !== null) {
      clearTimeout(batchScrollEventsTimer.current);
    }

    batchScrollEventsTimer.current = window.setTimeout(() => {
      const containerElement = startOfStream?.parentElement;
      const momentElements =
        containerElement?.querySelectorAll(".moment-summary") || [];
      const containerTop = containerElement?.getBoundingClientRect()?.top || 0;
      const containerBottom =
        containerElement?.getBoundingClientRect()?.bottom || 0;

      const firstVisibleMomentElement =
        Array.from(momentElements).find((element) => {
          const { top: momentTop } = element.getBoundingClientRect();
          return containerTop <= momentTop && momentTop <= containerBottom;
        }) ||
        Array.from(momentElements).findLast((element) => {
          const { top: momentTop } = element.getBoundingClientRect();
          return momentTop < containerTop;
        });

      const dataMomentId =
        firstVisibleMomentElement?.getAttribute("data-moment-id");
      if (dataMomentId) {
        const momentId = Number.parseInt(dataMomentId);
        setCurrentMomentIndex(
          moments.findIndex((moment) => moment.id === momentId)
        );
      }
      batchScrollEventsTimer.current = null;
    }, 500);
  }, [ignoreScrollEventsTimer, moments, startOfStream, setCurrentMomentIndex]);

  useEffect(() => {
    // Register scroll listener
    startOfStream?.parentElement?.addEventListener("scroll", handleScroll);

    return () => {
      // Deregister scroll listener
      startOfStream?.parentElement?.removeEventListener("scroll", handleScroll);
    };
  }, [startOfStream, handleScroll]);

  const showPrevMoment = () => {
    if (currentMomentIndex <= 0 || !moments[currentMomentIndex - 1]) return;

    setCurrentMomentIndex(currentMomentIndex - 1);
    scrollToMoment(moments[currentMomentIndex - 1]?.id);
  };

  const showNextMoment = () => {
    if (
      currentMomentIndex >= moments.length - 1 ||
      !moments[currentMomentIndex + 1]
    )
      return;

    setCurrentMomentIndex(currentMomentIndex + 1);
    scrollToMoment(moments[currentMomentIndex + 1]?.id);
  };

  return (
    <MomentNavContainer style={{ textAlign: "right" }}>
      <ButtonGroup>
        <TooltipProvider tooltip={reverse ? LATEST_TOOLTIP : EARLIEST_TOOLTIP}>
          <Button
            className="bi-chevron-bar-up"
            variant="secondary"
            onClick={() => scrollToTop()}
            disabled={!hasMoments}
          />
        </TooltipProvider>
        <TooltipProvider tooltip="Previous">
          <Button
            className="bi-chevron-up"
            variant="secondary"
            onClick={() => showPrevMoment()}
            disabled={!hasMoments}
          />
        </TooltipProvider>
        <Button variant="secondary" disabled>
          {`Moment ${hasMoments ? moments[currentMomentIndex]?.id : "-"}`}
        </Button>
        <TooltipProvider tooltip="Next">
          <Button
            className="bi-chevron-down"
            variant="secondary"
            onClick={() => showNextMoment()}
            disabled={!hasMoments}
          />
        </TooltipProvider>
        <TooltipProvider tooltip={reverse ? EARLIEST_TOOLTIP : LATEST_TOOLTIP}>
          <Button
            className="bi-chevron-bar-down"
            variant="secondary"
            onClick={() => scrollToBottom()}
            disabled={!hasMoments}
          />
        </TooltipProvider>
        {!isRefetching && (
          <TooltipProvider tooltip="Refresh">
            <Button
              className="bi-arrow-clockwise"
              variant="secondary"
              onClick={() => refetch()}
              disabled={!hasMoments}
            />
          </TooltipProvider>
        )}
        {isRefetching && (
          <Button variant="secondary" disabled>
            <Spinner size="sm" />
          </Button>
        )}
      </ButtonGroup>
    </MomentNavContainer>
  );
};

export default MomentNavigator;
